import React, { useMemo } from "react";
import { Key } from "../../model_gen/key";
import { Button } from "../ui/button";
import { CopyIcon, EyeIcon, EyeOffIcon, EditIcon, TrashIcon } from "lucide-react";
import moment from "moment";
import { cn } from "../../lib/utils";
import PaginatedTable, { PaginatedTableColumn, SortConfig } from "../ui/paginated-table";

interface KeysTableProps {
  keys: Key[];
  loading: boolean;
  visibleKey: string | null;
  onToggleVisibility: (keyValue: string) => void;
  onCopyKey: (keyValue: string) => void;
  onEditKey: (key: Key) => void;
  onDeleteKey: (key: Key) => void;
  emptyTitle?: string;
  emptyDescription?: string;
  // Pagination props
  currentPage?: number;
  onPageChange?: (page: number) => void;
  pageSize?: number;
  totalItems?: number;
  showPagination?: boolean;
  sortConfig?: SortConfig<Key>;
  onSort?: (key: keyof Key) => void;
}

const KeysTable: React.FC<KeysTableProps> = ({
  keys,
  loading,
  visibleKey,
  onToggleVisibility,
  onCopyKey,
  onEditKey,
  onDeleteKey,
  emptyTitle = "No API keys found",
  emptyDescription = "Create your first API key to get started with provider integrations.",
  currentPage = 1,
  onPageChange = () => {},
  pageSize = 10,
  totalItems,
  showPagination = false,
  sortConfig,
  onSort,
}) => {
  const formatDate = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "Unknown";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const getKeyTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      "Maitai": "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
      "OpenAI": "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
      "Anthropic": "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400",
      "Groq": "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400",
      "Cerebras": "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
      "Sambanova": "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
      "Gemini": "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400",
      "Deepseek": "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/20 dark:text-cyan-400",
    };
    return colors[type] || "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
  };

  const renderKeyValue = (key: Key) => {
    if (key.obfuscated) {
      return (
        <div className="text-sm font-mono text-muted-foreground">
          {key.key_value}
        </div>
      );
    }

    const isVisible = visibleKey === key.key_value;
    const displayValue = isVisible ? key.key_value : "•".repeat(32);

    return (
      <div className="flex items-center gap-2">
        <code className="text-sm font-mono bg-muted px-2 py-1 rounded text-foreground max-w-[200px] truncate">
          {displayValue}
        </code>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={(e) => {
              e.stopPropagation();
              onCopyKey(key.key_value || "");
            }}
          >
            <CopyIcon className="w-3 h-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={(e) => {
              e.stopPropagation();
              onToggleVisibility(key.key_value || "");
            }}
          >
            {isVisible ? (
              <EyeOffIcon className="w-3 h-3" />
            ) : (
              <EyeIcon className="w-3 h-3" />
            )}
          </Button>
        </div>
      </div>
    );
  };

  const columns: PaginatedTableColumn<Key>[] = useMemo(() => [
    {
      key: "type",
      header: "Key Type",
      width: "w-[20%]",
      sortable: true,
      render: (key: Key) => (
        <span className={cn(
          "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
          getKeyTypeColor(key.type || "")
        )}>
          {key.type || "Unknown"}
        </span>
      ),
    },
    {
      key: "description",
      header: "Description",
      width: "w-[25%]",
      sortable: true,
      render: (key: Key) => (
        <div className="text-sm font-gc-reg text-foreground">
          {key.description || "-"}
        </div>
      ),
    },
    {
      key: "key_value",
      header: "Key Value",
      width: "w-[30%]",
      render: (key: Key) => renderKeyValue(key),
    },
    {
      key: "date_created",
      header: "Date Created",
      width: "w-[15%]",
      sortable: true,
      render: (key: Key) => (
        key.date_created ? (
          <div className="space-y-1">
            <div className="text-sm font-gc-reg text-foreground">
              {formatDate(key.date_created)}
            </div>
            <div className="text-xs text-muted-foreground">
              {formatTime(key.date_created)}
            </div>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">Unknown</span>
        )
      ),
    },
    {
      key: "id",
      header: "Actions",
      width: "w-[10%]",
      render: (key: Key) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1"
            onClick={(e) => {
              e.stopPropagation();
              onEditKey(key);
            }}
          >
            <EditIcon className="w-3 h-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteKey(key);
            }}
          >
            <TrashIcon className="w-3 h-3" />
          </Button>
        </div>
      ),
    },
  ], [visibleKey, onCopyKey, onToggleVisibility, onEditKey, onDeleteKey]);

  const defaultSort = (a: Key, b: Key) => {
    // Default sort by date_created descending
    const dateA = a.date_created || 0;
    const dateB = b.date_created || 0;
    return dateB - dateA;
  };

  return (
    <PaginatedTable
      data={keys}
      columns={columns}
      loading={loading}
      sortConfig={sortConfig}
      onSort={onSort}
      currentPage={currentPage}
      onPageChange={onPageChange}
      pageSize={pageSize}
      totalItems={totalItems}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
      showPagination={showPagination}
      loadingRows={5}
      defaultSort={defaultSort}
      rowClassName="cursor-pointer hover:bg-accent/20 transition-colors"
    />
  );
};

export default KeysTable; 